import { useQuery } from '@tanstack/react-query';
import { Bell, BookOpen, HelpCircle, Megaphone, MessageCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';
import { useSocket } from '../SocketProvider';
import { ChangelogPopover } from './ChangelogPopover';
import { EmbedStatusIndicator } from './EmbedStatusIndicator';
import { TopBarUserMenu } from './TopBarUserMenu';
import { QueryKey } from '@/Config/api/queryKey';
import { SocketKey } from '@/Config/api/socketKey';
import { pushGtmEvent } from '@/Config/libs/analytics';
import { KnowledgeBase } from '@/Domain/api/KnowledgeBase/dto/KnowledgeBase.dto';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/Domain/components/Shared/Popover/Popover';
import { SidebarTrigger } from '@/Domain/components/Shared/Sidebar/Sidebar';
import { AutosizeTextarea } from '@/Domain/components/Shared/Textarea/Textarea';
import { AnimatedThemeToggler } from '@/Domain/components/Shared/ThemeSelect/AnimatedThemeToggler';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { UpgradePlanButton } from '@/Domain/components/UpgradePlanButton/UpgradePlanButton';
import { GtmEventEnum } from '@/Domain/enum/GtmEvent.enum';
import { useChangelog } from '@/Domain/hooks/Changelog/useChangelog.hook';
import { useMobilePreviewStore } from '@/Domain/store/mobilePreview.store';
import { useSupportWidgetStore } from '@/Domain/store/supportWidget.store';

const LOCAL_STORAGE_ENTRY_NAME = '__qc_conversation';
const LOCAL_STORAGE_ENTRY_NAME_ENCRYPTED = `${LOCAL_STORAGE_ENTRY_NAME}_encrypted`;
const WIDGET_ORIGIN = new URL(import.meta.env.VITE_WIDGET_URL || 'https://bubble.quickchat.ai')
  .origin;

export const TOP_BAR_HEIGHT = 53;
export const TopBar = () => {
  const { t } = useTranslation('topbar');
  const { socket } = useSocket();
  const {
    open: isWidgetOpen,
    toggleOpen,
    openWithMessage,
    feedbackPopoverOpen,
    setFeedbackPopoverOpen,
  } = useSupportWidgetStore();
  const openPreview = useMobilePreviewStore((s) => s.openPreview);
  const [feedbackText, setFeedbackText] = useState('');
  const changelog = useChangelog();

  const { scenario } = useParams();
  const localStorageItemName = `${LOCAL_STORAGE_ENTRY_NAME_ENCRYPTED}_${scenario ?? ''}`;
  const [, setConversation, removeConversation] = useLocalStorage(localStorageItemName, null);

  const retrain = useQuery<KnowledgeBase>({ queryKey: [QueryKey.knowledgeBase.get] });
  const isLegacyRetrain = retrain.data?.atomic_embed_enabled === false;
  const pendingEmbedCount = retrain.data?.pending_embed_count ?? 0;
  const failedEmbedCount = retrain.data?.failed_embed_count ?? 0;

  const handeDocumentationClick = () =>
    window.open('https://docs.quickchat.ai/', '_blank', 'noopener,noreferrer');

  useEffect(() => {
    if (isWidgetOpen) {
      const onKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          toggleOpen();
        }
      };

      window.addEventListener('keydown', onKeyDown);
      return () => {
        window.removeEventListener('keydown', onKeyDown);
      };
    }
  }, [isWidgetOpen, toggleOpen]);

  useEffect(() => {
    const handleKnowledgeBaseStateChanged = () => {
      void retrain.refetch();
    };

    socket.on(SocketKey.knowledgeBase.articleEmbedStateChanged, handleKnowledgeBaseStateChanged);
    // Legacy agents never emit articleEmbedStateChanged, so this is the only signal that refreshes their staleTime:Infinity KB cache.
    socket.on(SocketKey.knowledgeBase.retrainStateChanged, handleKnowledgeBaseStateChanged);

    return () => {
      socket.off(SocketKey.knowledgeBase.articleEmbedStateChanged, handleKnowledgeBaseStateChanged);
      socket.off(SocketKey.knowledgeBase.retrainStateChanged, handleKnowledgeBaseStateChanged);
    };
  }, [socket, retrain]);

  const previewGtmFired = useRef(false);
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== WIDGET_ORIGIN) return;
      const isPreviewSource = Array.from(
        document.querySelectorAll<HTMLIFrameElement>('iframe[data-quickchat-preview]'),
      ).some((iframe) => event.source === iframe.contentWindow);
      if (!isPreviewSource) return;
      const messageType = event.data?.type;
      // The preview widget iframe posts these as the visitor starts or resets a conversation.
      if (messageType === 'quickchat_conversation') {
        const convId = event.data.data?.conv_id;
        if (convId) {
          setConversation(convId);
          if (!previewGtmFired.current) {
            previewGtmFired.current = true;
            pushGtmEvent(GtmEventEnum.PreviewConversationStarted, { scenario_id: scenario });
          }
        }
      }
      if (messageType === 'quickchat_reset') {
        removeConversation();
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [setConversation, removeConversation, scenario]);

  return (
    <>
      <div
        className="flex h-13 shrink-0 items-center justify-between gap-2 border-b border-sidebar-border bg-sidebar pl-4 pr-4 md:pl-6"
        style={{ height: TOP_BAR_HEIGHT }}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <SidebarTrigger className="md:hidden" />
          {scenario && (
            <Button
              variant="secondary"
              size="xs"
              className="md:hidden h-8 shrink-0 gap-1.5 rounded-xl px-3 text-sm"
              onClick={openPreview}
              data-testid="topbar-preview-button"
            >
              <MessageCircle className="size-4" />
              {t('button.preview')}
            </Button>
          )}
          {!isLegacyRetrain ? (
            <EmbedStatusIndicator
              pendingEmbedCount={pendingEmbedCount}
              failedEmbedCount={failedEmbedCount}
            />
          ) : (
            <div />
          )}
        </div>
        <TooltipProvider>
          <div className="flex items-center gap-2 md:gap-0.5">
            <UpgradePlanButton
              size="xs"
              className="md:hidden rounded-xl !px-4 !h-8 !text-sm"
              source="top_bar"
            />
            <Button
              variant="ghost"
              size="xs"
              className="hidden h-8 gap-1.5 rounded-xl px-2.5 text-sm text-muted-foreground md:inline-flex"
              onClick={toggleOpen}
            >
              <HelpCircle />
              {t('button.need_help')}
            </Button>
            <Popover open={feedbackPopoverOpen} onOpenChange={setFeedbackPopoverOpen}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      size="xs"
                      className="hidden h-8 w-8 rounded-full p-2 text-muted-foreground md:inline-flex"
                      aria-label={t('button.feedback')}
                      data-testid="topbar-feedback-button"
                    >
                      <Megaphone />
                    </Button>
                  </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent>{t('button.feedback')}</TooltipContent>
              </Tooltip>
              <PopoverContent
                align="end"
                sideOffset={8}
                className="w-80 p-3"
                data-testid="topbar-feedback-popover"
                onOpenAutoFocus={(event) => {
                  // Radix's default focuses the first focusable element with the
                  // caret at position 0; for a re-opened popover with persisted
                  // draft text we want the caret at the end so users can keep typing.
                  event.preventDefault();
                  const textarea = (event.currentTarget as HTMLElement).querySelector('textarea');
                  if (textarea) {
                    textarea.focus();
                    const len = textarea.value.length;
                    textarea.setSelectionRange(len, len);
                  }
                }}
              >
                <div className="flex flex-col gap-3">
                  <AutosizeTextarea
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    placeholder={t('feedback_popover.placeholder')}
                    minHeight={116}
                    maxHeight={300}
                    className="!py-2 !px-3 text-sm"
                    data-testid="topbar-feedback-textarea"
                  />
                  <Button
                    size="sm"
                    className="w-full"
                    disabled={!feedbackText.trim()}
                    onClick={() => {
                      openWithMessage(`Feedback message: ${feedbackText.trim()}`);
                      setFeedbackText('');
                      setFeedbackPopoverOpen(false);
                    }}
                    data-testid="topbar-feedback-send"
                  >
                    {t('feedback_popover.send')}
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="xs"
                  className="hidden h-8 w-8 rounded-full p-2 text-muted-foreground md:inline-flex"
                  onClick={handeDocumentationClick}
                  aria-label={t('button.documentation')}
                >
                  <BookOpen />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('button.documentation')}</TooltipContent>
            </Tooltip>
            <Popover
              onOpenChange={(open) => {
                if (open) changelog.markAllSeen();
              }}
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      size="xs"
                      className="relative hidden h-8 w-8 rounded-full p-2 text-muted-foreground md:inline-flex"
                      aria-label={t('button.changelog')}
                      data-testid="topbar-changelog-button"
                    >
                      <Bell />
                      {changelog.hasUnseen && (
                        <span
                          className="absolute right-1 top-1 size-2 rounded-full bg-info"
                          data-testid="topbar-changelog-unseen-dot"
                        />
                      )}
                    </Button>
                  </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent>{t('button.changelog')}</TooltipContent>
              </Tooltip>
              <PopoverContent align="end" sideOffset={8} className="w-96 p-0">
                <ChangelogPopover
                  entries={changelog.entries}
                  isLoading={changelog.isLoading}
                  isError={changelog.isError}
                />
              </PopoverContent>
            </Popover>
            <AnimatedThemeToggler className="hidden !size-8 !p-2 rounded-full md:inline-flex" />
            <TopBarUserMenu />
          </div>
        </TooltipProvider>
      </div>
    </>
  );
};
